/**
 * Runtime environment configuration
 * Reads from window.__ENV__ (injected at container startup) with fallback to import.meta.env (build time)
 */

declare const __APP_VERSION__: string;

declare global {
  interface Window {
    __ENV__?: {
      VITE_API_URL?: string;
      VITE_APP_NAME?: string;
      VITE_APP_LOGO?: string;

      VITE_DEFAULT_LANGUAGE?: string;
      VITE_ENABLE_ROW_CLICK_EDIT?: string;
      VITE_DASHBOARD_TYPE?: string;
      VITE_SITE_SCOPING_ENABLED?: string;
      VITE_VISIBLE_MENU_ITEMS?: string;
      VITE_EXCLUDED_TOC_NAMES?: string;
      VITE_USER_MANUAL_ENABLED?: string;
      VITE_HELP_CENTER_ENABLED?: string;
      VITE_MFA_ENABLED?: string;
      VITE_DIFY_ENABLED?: string;
      VITE_DIFY_TOKEN?: string;
      VITE_DIFY_BASE_URL?: string;
    };
  }
}

const getEnv = (key: string, defaultValue: string = ""): string => {
  // Runtime config takes precedence (Docker)
  if (
    typeof window !== "undefined" &&
    window.__ENV__?.[key as keyof typeof window.__ENV__]
  ) {
    return window.__ENV__[key as keyof typeof window.__ENV__] || defaultValue;
  }
  // Fallback to build-time env (local dev)
  return (import.meta.env[key] as string) || defaultValue;
};

export type DashboardType = "placeholder" | "crm";

export const ENV = {
  API_URL: getEnv("VITE_API_URL", "/api"),
  APP_NAME: getEnv("VITE_APP_NAME", "Sep Dashboard"),
  APP_LOGO: getEnv("VITE_APP_LOGO", "/logo.svg"),

  DEFAULT_LANGUAGE: getEnv("VITE_DEFAULT_LANGUAGE", "vi"),
  ENABLE_ROW_CLICK_EDIT:
    getEnv("VITE_ENABLE_ROW_CLICK_EDIT", "false") === "true",
  DASHBOARD_TYPE: getEnv("VITE_DASHBOARD_TYPE", "placeholder") as DashboardType,
  SITE_SCOPING_ENABLED: getEnv("VITE_SITE_SCOPING_ENABLED", "false") === "true",
  VISIBLE_MENU_ITEMS: getEnv("VITE_VISIBLE_MENU_ITEMS", "*")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean),
  EXCLUDED_TOC_NAMES: getEnv(
    "VITE_EXCLUDED_TOC_NAMES",
    [
      "com.aras.innovator.cui_default.toc_Administration",
      "com.aras.innovator.cui_default.toc_Change Management",
      "com.aras.innovator.cui_default.toc_Design",
      "com.aras.innovator.cui_default.toc_Documents",
      "com.aras.innovator.xpxc.toc_Extended Classification",
      "com.aras.innovator.cui_default.toc_My Innovator",
      "com.aras.innovator.cui_default.toc_Portfolio",
      "com.aras.innovator.cui_default.toc_Sourcing",
    ].join(","),
  )
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean),
  USER_MANUAL_ENABLED:
    getEnv("VITE_USER_MANUAL_ENABLED", "false") === "true",
  HELP_CENTER_ENABLED:
    getEnv("VITE_HELP_CENTER_ENABLED", "false") === "true",
  MFA_ENABLED: getEnv("VITE_MFA_ENABLED", "false") === "true",
  DIFY_ENABLED: getEnv("VITE_DIFY_ENABLED", "false") === "true",
  DIFY_TOKEN: getEnv("VITE_DIFY_TOKEN", ""),
  DIFY_BASE_URL: getEnv("VITE_DIFY_BASE_URL", ""),
  APP_VERSION:
    typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "0.0.0",
};
